import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import OpportunityForm from "./OpportunityForm";
import AlertDialog from "./AlertDialog";
import Select from "react-select";
import OpportunitiesCalendar from "./OpportunitiesCalendar";
import OpportunitiesAnalytics from "./OpportunitiesAnalytics";
import {
  TrashIcon,
  EyeIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const formatDate = (date) =>
  date ? new Date(date).toISOString().split("T")[0] : "N/A";

const formatCurrency = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const API = "https://erp-xe9u.onrender.com/api";

const STATUS_OPTIONS = [
  { value: "Undecided", label: "Undecided" },
  { value: "Lost", label: "Lost" },
  { value: "Won", label: "Won" },
  { value: "No Opportunity", label: "No Opportunity" },
];

const ESTIMATION_LABELS = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
};

const VIEWS = [
  { value: "list", label: "List" },
  { value: "calendar", label: "Calendar" },
  { value: "analytics", label: "Analytics" },
];

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#111827",
    borderColor: state.isFocused ? "#f97316" : "#374151",
    minHeight: "2.5rem",
    borderRadius: "0.5rem",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    "input:focus": { boxShadow: "none" },
  }),
  singleValue: (provided) => ({ ...provided, color: "#e5e7eb" }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
  }),
  multiValueLabel: (provided) => ({ ...provided, color: "#e5e7eb" }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": { backgroundColor: "#f97316", color: "#fff" },
  }),
  placeholder: (provided) => ({ ...provided, color: "#6b7280" }),
  clearIndicator: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": { color: "#f97316" },
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": { color: "#f97316" },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#374151" : "#1f2937",
    color: state.isSelected ? "#fb923c" : "#e5e7eb",
    "&:active": { backgroundColor: "#4b5563" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#1f2937",
    border: "1px solid #374151",
    borderRadius: "0.5rem",
    overflow: "hidden",
    zIndex: 30,
  }),
};

const btnSecondary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-gray-700 px-3 py-1.5 text-sm font-semibold text-gray-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500/50 transition-colors disabled:cursor-not-allowed disabled:opacity-40";

const BADGE_TONES = {
  green: {
    wrap: "bg-green-500/10 text-green-400 ring-green-500/20",
    dot: "fill-green-400",
  },
  red: {
    wrap: "bg-red-500/10 text-red-400 ring-red-500/20",
    dot: "fill-red-400",
  },
  yellow: {
    wrap: "bg-yellow-500/10 text-yellow-400 ring-yellow-500/20",
    dot: "fill-yellow-400",
  },
  gray: {
    wrap: "bg-gray-500/10 text-gray-300 ring-gray-500/20",
    dot: "fill-gray-400",
  },
};

function Badge({ tone = "gray", children }) {
  const t = BADGE_TONES[tone];
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-x-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        t.wrap,
      )}
    >
      <svg
        viewBox="0 0 6 6"
        aria-hidden="true"
        className={classNames("size-1.5", t.dot)}
      >
        <circle r={3} cx={3} cy={3} />
      </svg>
      {children}
    </span>
  );
}

const statusTone = (s) =>
  s === "Won"
    ? "green"
    : s === "Lost"
      ? "red"
      : s === "No Opportunity"
        ? "gray"
        : "yellow";

const estimationTone = (s) =>
  s === "completed" ? "green" : s === "in_progress" ? "yellow" : "gray";

function IconButton({ onClick, title, tone = "gray", children }) {
  const tones = {
    gray: "text-gray-400 hover:bg-gray-700 hover:text-gray-100",
    red: "text-red-400 hover:bg-red-500/10",
    orange: "text-orange-400 hover:bg-orange-500/10",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={classNames("rounded-lg p-1.5 transition-colors", tones[tone])}
    >
      {children}
    </button>
  );
}

function Spinner({ label }) {
  return (
    <div className="py-16 text-center">
      <div className="inline-block size-8 animate-spin rounded-full border-b-2 border-orange-500" />
      <p className="mt-3 text-sm text-gray-400">{label}</p>
    </div>
  );
}

function SortableTh({ label, active, order, onClick }) {
  return (
    <th
      scope="col"
      className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400"
    >
      <button
        onClick={onClick}
        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-100"
      >
        {label}
        {active &&
          (order === "desc" ? (
            <ChevronDownIcon className="size-4 text-orange-400" />
          ) : (
            <ChevronUpIcon className="size-4 text-orange-400" />
          ))}
      </button>
    </th>
  );
}

const pageBtn =
  "inline-flex items-center justify-center rounded-lg px-2.5 py-1.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40";

export default function OpportunitiesList({
  setOpportunities: setParentOpportunities,
  sortOrder: parentSortOrder,
  handleSort: parentHandleSort,
  navigate,
  users,
  accounts,
  contacts,
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
    showCancel: false,
  });

  // Filter state
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const savedStatuses = sessionStorage.getItem("opportunitiesFilter");
    return savedStatuses ? JSON.parse(savedStatuses) : ["Undecided"];
  });

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // View state
  const [view, setView] = useState("list");

  // Sort state - managed locally for server-side sorting
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 50;

  // Opportunities data - single source of truth
  const [opportunities, setOpportunities] = useState([]);
  const opportunitiesRef = useRef([]);

  // All opportunities for calendar/analytics (loaded separately when needed)
  const [allOpportunities, setAllOpportunities] = useState([]);
  const [loadingAllOpportunities, setLoadingAllOpportunities] = useState(false);

  // Calendar visibility toggles
  const [visibleEventTypes, setVisibleEventTypes] = useState({
    estimateDueDate: true,
    proposalSentDate: true,
    activity: true,
  });

  // Abort controller ref for cancelling requests
  const abortControllerRef = useRef(null);

  const closeAlert = () => setAlert((prev) => ({ ...prev, open: false }));

  // Persist filter to session storage
  useEffect(() => {
    sessionStorage.setItem(
      "opportunitiesFilter",
      JSON.stringify(selectedStatuses),
    );
  }, [selectedStatuses]);

  // Fetch paginated opportunities from server
  const fetchOpportunities = useCallback(
    async (page = 1, signal) => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }

        const params = new URLSearchParams({
          page: page.toString(),
          limit: itemsPerPage.toString(),
          sortField,
          sortOrder,
        });

        if (selectedStatuses.length > 0) {
          params.append("status", selectedStatuses.join(","));
        }

        const response = await axios.get(
          `${API}/opportunities?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal,
          },
        );

        const fetchedOpportunities = Array.isArray(response.data.opportunities)
          ? response.data.opportunities
          : [];

        opportunitiesRef.current = fetchedOpportunities;
        setOpportunities(fetchedOpportunities);
        setParentOpportunities?.(fetchedOpportunities);
        setTotalItems(response.data.total || 0);
        setTotalPages(response.data.pages || 1);
        setCurrentPage(response.data.currentPage || page);
      } catch (err) {
        // Don't show an error if the request was cancelled
        if (axios.isCancel(err) || err.name === "AbortError") {
          return;
        }

        setError(
          err.response?.data?.message ||
            "Failed to fetch opportunities. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    },
    [
      selectedStatuses,
      sortField,
      sortOrder,
      itemsPerPage,
      setParentOpportunities,
    ],
  );

  // Fetch ALL opportunities for calendar/analytics views
  const fetchAllOpportunities = useCallback(async () => {
    setLoadingAllOpportunities(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const params = new URLSearchParams({
        page: "1",
        limit: "1000",
        sortField,
        sortOrder,
      });

      if (selectedStatuses.length > 0) {
        params.append("status", selectedStatuses.join(","));
      }

      const response = await axios.get(
        `${API}/opportunities?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setAllOpportunities(
        Array.isArray(response.data.opportunities)
          ? response.data.opportunities
          : [],
      );
    } catch (err) {
      console.error("Failed to fetch all opportunities:", err);
      // Fall back to the current page's data
      setAllOpportunities(opportunitiesRef.current);
    } finally {
      setLoadingAllOpportunities(false);
    }
  }, [selectedStatuses, sortField, sortOrder]);

  // Fetch opportunities when filters, sort, or page changes
  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    fetchOpportunities(currentPage, abortControllerRef.current.signal);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [currentPage, selectedStatuses, sortField, sortOrder, fetchOpportunities]);

  // Fetch all opportunities when switching to calendar or analytics view
  useEffect(() => {
    if (view === "calendar" || view === "analytics") {
      fetchAllOpportunities();
    }
  }, [view, fetchAllOpportunities]);

  const handleDeleteConfirm = (opportunityId) => {
    setAlert({
      open: true,
      type: "warning",
      title: "Confirm Delete",
      message:
        "Are you sure you want to delete this opportunity? This action cannot be undone.",
      onConfirm: () => handleDelete(opportunityId),
      showCancel: true,
    });
  };

  const handleDelete = async (opportunityId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      await axios.delete(`${API}/opportunities/${opportunityId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Update local state immediately for better UX
      setOpportunities((prev) =>
        prev.filter((opp) => opp._id !== opportunityId),
      );
      setParentOpportunities?.((prev) =>
        prev.filter((opp) => opp._id !== opportunityId),
      );
      setTotalItems((prev) => prev - 1);

      // If current page becomes empty, go to previous page
      if (opportunities.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      } else {
        fetchOpportunities(currentPage);
      }

      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Opportunity deleted successfully!",
        onConfirm: closeAlert,
        showCancel: false,
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          err.response?.data?.message ||
          "Failed to delete opportunity: " + err.message,
        onConfirm: closeAlert,
        showCancel: false,
      });
    }
  };

  const handleStatusFilterChange = (selected) => {
    setSelectedStatuses(selected ? selected.map((option) => option.value) : []);
    setCurrentPage(1);
  };

  const handleTitleSort = () => {
    const newSortOrder =
      sortField === "title" && sortOrder === "asc" ? "desc" : "asc";
    setSortField("title");
    setSortOrder(newSortOrder);
    setCurrentPage(1);
  };

  const handleDateSort = () => {
    const newSortOrder =
      sortField === "createdAt" && sortOrder === "desc" ? "asc" : "desc";
    setSortField("createdAt");
    setSortOrder(newSortOrder);
    setCurrentPage(1);
  };

  const toggleEventType = (eventType) => {
    setVisibleEventTypes((prev) => ({
      ...prev,
      [eventType]: !prev[eventType],
    }));
  };

  const handleRowClick = (opportunityId, event) => {
    if (event.target.closest(".actions-column")) {
      return;
    }
    navigate(`/Opportunities/${opportunityId}`);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 2) {
        end = 4;
      }
      if (currentPage >= totalPages - 1) {
        start = totalPages - 3;
      }

      if (start > 2) {
        pages.push("...");
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push("...");
      }

      pages.push(totalPages);
    }

    return pages;
  };

  const rangeStart =
    totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(currentPage * itemsPerPage, totalItems);

  const calendarLegend = [
    { key: "estimateDueDate", label: "Estimate", dot: "bg-blue-300" },
    { key: "proposalSentDate", label: "Proposal", dot: "bg-green-300" },
    { key: "activity", label: "Activity", dot: "bg-purple-300" },
  ];

  return (
    <div className="mx-auto max-w-full">
      <div className="rounded-2xl bg-gray-900 p-4 shadow-xl ring-1 ring-white/10 sm:p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-orange-400">
                Pipeline
              </p>
              <h1 className="text-2xl font-bold text-white">Opportunities</h1>
            </div>
            {view === "list" && (
              <span className="rounded-full bg-gray-800 px-2.5 py-1 text-xs font-semibold text-gray-300 ring-1 ring-inset ring-white/10">
                {totalItems} total
              </span>
            )}
          </div>

          <div className="flex rounded-lg bg-gray-800 p-1 ring-1 ring-white/10">
            {VIEWS.map((v) => (
              <button
                key={v.value}
                onClick={() => setView(v.value)}
                className={classNames(
                  "flex-1 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors",
                  view === v.value
                    ? "bg-orange-500 text-white shadow"
                    : "text-gray-400 hover:bg-gray-700 hover:text-gray-100",
                )}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {/* Status filter - shown for all views */}
        <div className="mb-6 rounded-xl bg-gray-800 p-4 ring-1 ring-white/10">
          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-400">
            Filter by Status
          </label>
          <Select
            isMulti
            options={STATUS_OPTIONS}
            value={STATUS_OPTIONS.filter((option) =>
              selectedStatuses.includes(option.value),
            )}
            onChange={handleStatusFilterChange}
            placeholder="All statuses"
            className="w-full md:max-w-lg"
            isClearable
            isSearchable={false}
            styles={customSelectStyles}
          />
        </div>

        {view === "list" && (
          <>
            {error && (
              <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-300 ring-1 ring-red-500/20">
                <span>{error}</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => fetchOpportunities(currentPage)}
                    className="font-semibold text-orange-400 hover:text-orange-300"
                  >
                    Retry
                  </button>
                  <button
                    onClick={() => setError("")}
                    className="text-red-300 hover:text-red-100"
                    aria-label="Dismiss"
                  >
                    <XMarkIcon className="size-5" />
                  </button>
                </div>
              </div>
            )}

            {isCreating && (
              <OpportunityForm
                setOpportunities={setOpportunities}
                setIsCreating={setIsCreating}
                users={users}
                accounts={accounts}
                contacts={contacts}
                navigate={navigate}
              />
            )}

            {loading ? (
              <Spinner label="Loading opportunities..." />
            ) : opportunities.length === 0 ? (
              <div className="rounded-xl bg-gray-800 px-6 py-14 text-center ring-1 ring-white/10">
                <p className="text-sm font-semibold text-gray-200">
                  No opportunities found
                </p>
                <p className="mt-1 text-sm text-gray-400">
                  {selectedStatuses.length > 0
                    ? "Try changing or clearing the status filter."
                    : "Opportunities will show up here once leads are qualified."}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-hidden rounded-xl bg-gray-800 ring-1 ring-white/10">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-white/10">
                      <thead className="bg-gray-800">
                        <tr>
                          <SortableTh
                            label="Title"
                            active={sortField === "title"}
                            order={sortOrder}
                            onClick={handleTitleSort}
                          />
                          {[
                            "Status",
                            "Estimation",
                            "Revenue",
                            "Proposal Sent",
                            "Estimate Due",
                            "Estimator",
                            "Account",
                          ].map((h) => (
                            <th
                              key={h}
                              scope="col"
                              className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400"
                            >
                              {h}
                            </th>
                          ))}
                          <SortableTh
                            label="Created"
                            active={sortField === "createdAt"}
                            order={sortOrder}
                            onClick={handleDateSort}
                          />
                          <th
                            scope="col"
                            className="actions-column whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-400"
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {opportunities.map((opportunity) => {
                          const accountName = opportunity.account?.companyName;
                          return (
                            <tr
                              key={opportunity._id}
                              className="cursor-pointer transition-colors hover:bg-gray-700/50"
                              title={opportunity.title}
                              onClick={(e) =>
                                handleRowClick(opportunity._id, e)
                              }
                            >
                              <td className="max-w-xs px-4 py-3">
                                <p className="truncate text-sm font-semibold text-gray-100">
                                  {opportunity.title || "Untitled"}
                                </p>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3">
                                <Badge tone={statusTone(opportunity.status)}>
                                  {opportunity.status || "N/A"}
                                </Badge>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3">
                                <Badge
                                  tone={estimationTone(
                                    opportunity.estimationStatus,
                                  )}
                                >
                                  {ESTIMATION_LABELS[
                                    opportunity.estimationStatus
                                  ] ||
                                    opportunity.estimationStatus ||
                                    "N/A"}
                                </Badge>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-100">
                                {formatCurrency(opportunity.estimatedRevenue)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-300">
                                {formatDate(opportunity.proposalSentDate)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-300">
                                {formatDate(opportunity.estimateDueDate)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-300">
                                {opportunity.estimator?.displayName || "N/A"}
                              </td>
                              <td
                                className="max-w-[12rem] truncate px-4 py-3 text-sm text-gray-300"
                                title={accountName}
                              >
                                {accountName || "N/A"}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-300">
                                {formatDate(opportunity.createdAt)}
                              </td>
                              <td className="actions-column whitespace-nowrap px-4 py-3">
                                <div className="flex items-center justify-end gap-0.5">
                                  <IconButton
                                    title="View"
                                    tone="orange"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `/Opportunities/${opportunity._id}`,
                                      );
                                    }}
                                  >
                                    <EyeIcon className="size-5" />
                                  </IconButton>
                                  <IconButton
                                    title="Delete"
                                    tone="red"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteConfirm(opportunity._id);
                                    }}
                                  >
                                    <TrashIcon className="size-5" />
                                  </IconButton>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
                  <p className="text-sm text-gray-400">
                    Showing{" "}
                    <span className="font-semibold text-gray-200">
                      {rangeStart}–{rangeEnd}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-gray-200">
                      {totalItems}
                    </span>{" "}
                    results
                  </p>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className={classNames(
                        pageBtn,
                        "bg-gray-700 text-gray-200 hover:bg-gray-600",
                      )}
                      title="First page"
                      aria-label="First page"
                    >
                      <ChevronDoubleLeftIcon className="size-4" />
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={btnSecondary}
                    >
                      <ChevronLeftIcon className="size-4" />
                      Previous
                    </button>

                    <div className="hidden gap-1 sm:flex">
                      {getPageNumbers().map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`ellipsis-${index}`}
                            className="px-2 py-1.5 text-sm text-gray-500"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            aria-current={
                              page === currentPage ? "page" : undefined
                            }
                            className={classNames(
                              pageBtn,
                              page === currentPage
                                ? "bg-orange-500 text-white shadow"
                                : "bg-gray-700 text-gray-200 hover:bg-gray-600",
                            )}
                          >
                            {page}
                          </button>
                        ),
                      )}
                    </div>

                    <span className="px-3 py-1 text-sm text-gray-400 sm:hidden">
                      {currentPage} / {totalPages}
                    </span>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={btnSecondary}
                    >
                      Next
                      <ChevronRightIcon className="size-4" />
                    </button>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className={classNames(
                        pageBtn,
                        "bg-gray-700 text-gray-200 hover:bg-gray-600",
                      )}
                      title="Last page"
                      aria-label="Last page"
                    >
                      <ChevronDoubleRightIcon className="size-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {view === "calendar" && (
          <div className="rounded-xl bg-gray-800 p-4 ring-1 ring-white/10">
            <div className="mb-4 flex flex-wrap gap-2">
              {calendarLegend.map((item) => (
                <label
                  key={item.key}
                  className={classNames(
                    "inline-flex cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ring-1 ring-inset transition-colors",
                    visibleEventTypes[item.key]
                      ? "bg-gray-700 text-gray-100 ring-white/10"
                      : "bg-gray-900 text-gray-500 ring-white/5",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={visibleEventTypes[item.key]}
                    onChange={() => toggleEventType(item.key)}
                    className="size-4 rounded border-gray-600 bg-gray-700 text-orange-500 focus:ring-orange-500"
                  />
                  {item.label}
                  <span
                    className={classNames(
                      "inline-block size-2.5 rounded-full",
                      item.dot,
                    )}
                  />
                </label>
              ))}
            </div>

            {loadingAllOpportunities ? (
              <Spinner label="Loading calendar data..." />
            ) : (
              <OpportunitiesCalendar
                opportunities={allOpportunities}
                users={users}
                navigate={navigate}
                visibleEventTypes={visibleEventTypes}
                toggleEventType={toggleEventType}
              />
            )}
          </div>
        )}

        {view === "analytics" &&
          (loadingAllOpportunities ? (
            <Spinner label="Loading analytics data..." />
          ) : (
            <OpportunitiesAnalytics opportunities={allOpportunities} />
          ))}

        <AlertDialog
          open={alert.open}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={closeAlert}
          onConfirm={alert.onConfirm}
          showCancel={alert.showCancel}
        />
      </div>
    </div>
  );
}
